// src/lib/tutor/drill-follow-through.ts
//
// WHAT A FINISHED SAGE DRILL ESTABLISHES, AND THE ONE THING TO DO NEXT.
//
// Product review: a student finishes the drill Sage recommended and the loop
// just stops. The results screen says 2/5 and offers "Practice more"; Sage, if
// they come back, greets them like nothing happened. The follow-through a tutor
// would give — "2/5 on Boundaries: the gap is still there, and every miss was
// a comma splice; read the lesson, then we'll retry" — never came.
//
// This module is the rule, kept out of the model's hands:
//
//   accuracy (correct / answered)   evidence     next step
//   ≥ 80%                           < 5 answers  a fresh drill, same level (confirm it)
//   ≥ 80%, an easy (E) drill        ≥ 5          a fresh drill one level up (M)
//   ≥ 80%, a learning-path topic    ≥ 5          the topic's skill check
//   ≥ 80%, any other topic          ≥ 5          a fresh drill one level up (H at most)
//   50–79%                          any          a fresh retry drill, same level
//   < 50%, lesson exists, unread    any          the lesson
//   < 50%, otherwise                any          a fresh drill on easy (E) questions
//
// "Fresh" is offer_activity's own behaviour: pickDrillQuestions prefers
// questions the student hasn't seen. Every next step is on the SAME topic, so
// it always passes offer_activity's student-wording check (the student's
// follow-through message names that topic).
//
// The verdict text and the next step feed Sage's prompt
// (formatFollowThroughBlock); the server still builds the button through
// offer_activity. Sage never writes the link, and doesn't pick the step.
//
// Pure: no DB, no clock. The loader is drill-follow-through-context.ts.

import { stemExcerpt } from './nudge-bus'

export type DrillDifficulty = 'E' | 'M' | 'H' | 'mixed'

/** At or above this accuracy, the drill says the skill held. */
export const SOLID_AT = 0.8
/** Below this, the gap is still there. Between the two: partly there. */
export const PARTIAL_AT = 0.5
/** Fewer answers than this is a thin sample: a good run is "promising", not "solid". */
export const THIN_EVIDENCE_BELOW = 5

/**
 * How long after finishing a Sage drill Sage leads with the follow-through
 * when the student next writes, unprompted. After that the moment has passed.
 */
export const FOLLOW_THROUGH_WINDOW_MS = 2 * 60 * 60 * 1000
/**
 * How far back the results screen's "What does this mean?" looks for the
 * drill's answers. The student is looking at the result, so the answers are
 * minutes old; a day covers a results tab left open overnight.
 */
export const EXPLICIT_LOOKBACK_MS = 24 * 60 * 60 * 1000

/** Drill sizes offer_activity accepts (DRILL_COUNT in @unlimitedtests/core). */
const NEXT_DRILL_MIN = 5
const NEXT_DRILL_MAX = 15

/* ------------------------------------------------------------------ */
/*  The drill's result                                                  */
/* ------------------------------------------------------------------ */

export type DrillAnswer = {
  questionId: string
  answeredAt: Date
  correct: boolean
  /** Seconds-level timing from the stopwatch; null when unknown. */
  timeMs?: number | null
  /** The question's E/M/H tier at answer time; null when untagged. */
  difficulty?: 'E' | 'M' | 'H' | null
  /** The bank topic at answer time. */
  topic?: string | null
}

export type DrillOutcome = {
  /** Questions in the drill. */
  total: number
  /** Of those, how many have an answer counted. */
  answered: number
  correct: number
  /** When the last counted answer came in; null when none did. */
  finishedAt: Date | null
  /** The counted answer per question, in the drill's order. */
  answers: DrillAnswer[]
}

/**
 * The drill's result from the student's answers.
 *
 * 'first': each question's FIRST answer at or after `since` — the drill
 * attempt, as completed-actions counts it (a later retry isn't the drill).
 * 'latest': each question's LATEST answer at or after `since` — for the
 * results screen, where the drill just shown is the newest attempt at those
 * questions (an older drill may have served some of them before).
 */
export function drillOutcome(
  questionIds: readonly string[],
  answers: readonly DrillAnswer[],
  since: Date,
  pick: 'first' | 'latest',
): DrillOutcome {
  const ids = new Set(questionIds)
  const sorted = [...answers]
    .filter((a) => ids.has(a.questionId) && a.answeredAt >= since)
    .sort((a, b) => a.answeredAt.getTime() - b.answeredAt.getTime())
  const byId = new Map<string, DrillAnswer>()
  for (const a of sorted) {
    if (pick === 'first' && byId.has(a.questionId)) continue
    byId.set(a.questionId, a)
  }
  const counted = [...new Set(questionIds)]
    .map((id) => byId.get(id))
    .filter((a): a is DrillAnswer => !!a)
  const finishedAt = counted.reduce<Date | null>(
    (latest, a) => (!latest || a.answeredAt > latest ? a.answeredAt : latest),
    null,
  )
  return {
    total: new Set(questionIds).size,
    answered: counted.length,
    correct: counted.filter((a) => a.correct).length,
    finishedAt,
    answers: counted,
  }
}

/** Every question of the drill has an answer counted. */
export function isFinished(o: DrillOutcome): boolean {
  return o.total > 0 && o.answered >= o.total
}

/**
 * The drill's difficulty when its link didn't say: the one tier every answer
 * shares, else mixed.
 */
export function inferDifficulty(answers: readonly DrillAnswer[]): DrillDifficulty {
  const tiers = new Set(answers.map((a) => a.difficulty ?? null))
  if (tiers.size === 1) {
    const only = [...tiers][0]
    if (only === 'E' || only === 'M' || only === 'H') return only
  }
  return 'mixed'
}

/** The topic most answers were recorded under; null when none carry one. */
export function majorityTopic(answers: readonly DrillAnswer[]): string | null {
  const counts = new Map<string, number>()
  for (const a of answers) if (a.topic) counts.set(a.topic, (counts.get(a.topic) ?? 0) + 1)
  let best: string | null = null
  let bestN = 0
  for (const [t, n] of counts) {
    if (n > bestN) {
      best = t
      bestN = n
    }
  }
  return best
}

/* ------------------------------------------------------------------ */
/*  The rule                                                            */
/* ------------------------------------------------------------------ */

export type Verdict = 'promising' | 'solid' | 'partial' | 'gap'

export type NextStep =
  | { kind: 'skill_check'; topic: string }
  | { kind: 'lesson'; topic: string }
  | {
      kind: 'drill'
      topic: string
      difficulty: DrillDifficulty
      count: number
      /** Why this drill: the words Sage uses to frame it. */
      purpose: 'confirm' | 'step_up' | 'retry' | 'easier'
    }

export type FollowThroughInput = {
  topic: string
  difficulty: DrillDifficulty
  /** Questions in the drill. */
  total: number
  answered: number
  correct: number
  /** The topic is a learning-path step, so it has a skill check. */
  hasSkillCheck: boolean
  /** The topic has a lesson page, and whether the student has marked it read. */
  lesson: { available: boolean; read: boolean }
}

export type FollowThroughDecision = {
  verdict: Verdict
  /** correct / answered, 0–1. */
  accuracy: number
  /** Fewer than THIN_EVIDENCE_BELOW answers. */
  thin: boolean
  next: NextStep
}

const STEP_UP: Record<DrillDifficulty, DrillDifficulty> = {
  E: 'M',
  M: 'H',
  H: 'H',
  mixed: 'H',
}

function nextDrillCount(total: number): number {
  return Math.min(NEXT_DRILL_MAX, Math.max(NEXT_DRILL_MIN, total))
}

/**
 * The verdict and the one next step. Null when nothing was answered — there is
 * no result to explain.
 */
export function decideFollowThrough(input: FollowThroughInput): FollowThroughDecision | null {
  const { topic, difficulty, answered, correct } = input
  if (answered <= 0) return null
  const accuracy = Math.min(1, Math.max(0, correct / answered))
  const thin = answered < THIN_EVIDENCE_BELOW
  const count = nextDrillCount(input.total)

  if (accuracy >= SOLID_AT) {
    if (thin) {
      return {
        verdict: 'promising',
        accuracy,
        thin,
        next: { kind: 'drill', topic, difficulty, count, purpose: 'confirm' },
      }
    }
    if (difficulty !== 'E' && input.hasSkillCheck) {
      return { verdict: 'solid', accuracy, thin, next: { kind: 'skill_check', topic } }
    }
    return {
      verdict: 'solid',
      accuracy,
      thin,
      next: { kind: 'drill', topic, difficulty: STEP_UP[difficulty], count, purpose: 'step_up' },
    }
  }

  if (accuracy >= PARTIAL_AT) {
    return {
      verdict: 'partial',
      accuracy,
      thin,
      next: { kind: 'drill', topic, difficulty, count, purpose: 'retry' },
    }
  }

  if (input.lesson.available && !input.lesson.read) {
    return { verdict: 'gap', accuracy, thin, next: { kind: 'lesson', topic } }
  }
  return {
    verdict: 'gap',
    accuracy,
    thin,
    next: { kind: 'drill', topic, difficulty: 'E', count, purpose: 'easier' },
  }
}

/* ------------------------------------------------------------------ */
/*  The pattern in the misses                                           */
/* ------------------------------------------------------------------ */

function median(xs: number[]): number | null {
  if (xs.length === 0) return null
  const s = [...xs].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

/**
 * What the misses have in common, from what the answers record: the tier and
 * the time taken. Only says what the data shows; an empty list means no
 * pattern stands out (Sage then reads the missed questions themselves).
 */
export function missPatterns(answers: readonly DrillAnswer[]): string[] {
  const misses = answers.filter((a) => !a.correct)
  const hits = answers.filter((a) => a.correct)
  if (misses.length === 0) return []
  const out: string[] = []

  const missTiers = new Set(misses.map((a) => a.difficulty ?? null))
  const hitTiers = new Set(hits.map((a) => a.difficulty ?? null))
  if (missTiers.size === 1 && missTiers.has('H') && hits.length > 0 && !hitTiers.has('H')) {
    out.push('Every miss was a hard question; the easier ones held.')
  } else if (misses.some((a) => a.difficulty === 'E') && hits.some((a) => a.difficulty === 'H')) {
    out.push(
      'An easy question was missed while a hard one was right — more likely a slip or a misread rule than a ceiling.',
    )
  }

  const time = (xs: DrillAnswer[]) =>
    median(xs.map((a) => a.timeMs).filter((t): t is number => typeof t === 'number' && t > 0))
  const missMs = time(misses)
  const hitMs = time(hits)
  if (missMs !== null && hitMs !== null) {
    if (missMs < hitMs * 0.6) {
      out.push(
        `The misses came fast (about ${Math.round(missMs / 1000)}s each vs ${Math.round(hitMs / 1000)}s on the ones they got right) — likely rushing, or an answer that looked right at a glance.`,
      )
    } else if (missMs > hitMs * 1.5) {
      out.push(
        `The misses took much longer (about ${Math.round(missMs / 1000)}s each vs ${Math.round(hitMs / 1000)}s) — they got stuck on them, not careless.`,
      )
    }
  }
  return out
}

/* ------------------------------------------------------------------ */
/*  Words                                                               */
/* ------------------------------------------------------------------ */

const LEVEL_WORD: Record<DrillDifficulty, string> = {
  E: 'easy',
  M: 'medium',
  H: 'hard',
  mixed: 'mixed-difficulty',
}

/** What the result establishes, said plainly — the line Sage opens with. */
export function establishes(input: FollowThroughInput, d: FollowThroughDecision): string {
  const score = `${input.correct}/${input.answered}`
  const level = input.difficulty === 'mixed' ? '' : ` at the ${LEVEL_WORD[input.difficulty]} level`
  switch (d.verdict) {
    case 'promising':
      return `${score} on ${input.topic} — a good sign, but ${input.answered} ${input.answered === 1 ? 'question is' : 'questions are'} too few to call the skill solid. One more short set on fresh questions would tell.`
    case 'solid':
      return `${score} on ${input.topic} — enough to call this skill solid for now${level}.`
    case 'partial':
      return `${score} on ${input.topic} — partly there: the core is in place, but it isn't reliable yet.`
    case 'gap':
      return `${score} on ${input.topic} — the gap is still there.`
  }
}

/** The honest small-sample line, or null when the sample is big enough to speak for itself. */
export function evidenceCaveat(input: FollowThroughInput, d: FollowThroughDecision): string | null {
  const unfinished =
    input.answered < input.total
      ? `Only ${input.answered} of the drill's ${input.total} questions have an answer on record. `
      : ''
  if (d.thin) {
    return `${unfinished}${input.answered} ${input.answered === 1 ? 'answer' : 'answers'} is a small sample: treat it as a signal, not a verdict, and say so.`
  }
  if (input.answered < 10) {
    return `${unfinished}${input.answered} questions is a short drill: say it's a read on the skill "for now", not a final word.`
  }
  return unfinished ? unfinished.trim() : null
}

/** The offer_activity arguments for the next step. */
export function offerArgs(next: NextStep): Record<string, string | number> {
  switch (next.kind) {
    case 'drill':
      return { kind: 'drill', topic: next.topic, difficulty: next.difficulty, count: next.count }
    case 'lesson':
      return { kind: 'lesson', topic: next.topic }
    case 'skill_check':
      return { kind: 'skill_check', topic: next.topic }
  }
}

function nextStepWhy(next: NextStep): string {
  switch (next.kind) {
    case 'skill_check':
      return `The ${next.topic} skill check (10 questions) — the real test of whether it holds across the whole skill.`
    case 'lesson':
      return `The ${next.topic} lesson — they haven't read it, and the misses say the rule itself needs another look before more questions.`
    case 'drill': {
      const level = LEVEL_WORD[next.difficulty]
      switch (next.purpose) {
        case 'confirm':
          return `${next.count} fresh ${level} ${next.topic} questions, to confirm the result on questions they haven't seen.`
        case 'step_up':
          return `${next.count} fresh ${level} ${next.topic} questions — one level up, since this level held.`
        case 'retry':
          return `A retry: ${next.count} fresh ${level} ${next.topic} questions, after going over what the misses have in common.`
        case 'easier':
          return `${next.count} fresh easy ${next.topic} questions, to rebuild the basics before stepping back up.`
      }
    }
  }
}

export type FollowThroughFacts = {
  /**
   * 'asked': the student tapped "What does this mean?" on the drill's results
   * screen. 'lead': they finished it a little while ago and have just written
   * to Sage without being asked to.
   */
  mode: 'asked' | 'lead'
  /** "5 Boundaries questions". */
  label: string
  input: FollowThroughInput
  /** Null when there's no result to explain (nothing answered). */
  decision: FollowThroughDecision | null
  /** The missed questions: tier, seconds, the opening of the stem. */
  misses: Array<{
    position: number
    difficulty: 'E' | 'M' | 'H' | null
    seconds: number | null
    stem: string | null
  }>
  patterns: string[]
  /** The topic resolves to a catalog topic, so offer_activity can take it. */
  canOffer: boolean
}

/**
 * The system-prompt block. '' when there is nothing to follow through on.
 */
export function formatFollowThroughBlock(f: FollowThroughFacts): string {
  const { input, decision } = f
  const lines: string[] = ['DRILL FOLLOW-THROUGH:']

  if (!decision) {
    if (f.mode === 'lead') return ''
    lines.push(
      `The student asked what their result on the drill "${f.label}" means, but no answers to it are on record. Say you can't see the result yet, and ask how it went. Don't guess a score.`,
    )
    return lines.join('\n')
  }

  lines.push(
    f.mode === 'asked'
      ? `The student just finished the drill you offered — "${f.label}" — and tapped "What does this mean?" on its results screen. Their latest message is that question. This turn is the follow-through.`
      : `The student finished the drill you offered — "${f.label}" — a little while ago and hasn't heard from you since. If their latest message is a greeting, open-ended ("hi", "what now?", "what should I do next?") or about this drill, LEAD with the follow-through below instead of a generic greeting. If it asks about something specific and unrelated, answer that instead, mention the drill in one sentence at most, and don't offer the next step below.`,
  )

  const result = `Result (from their recorded answers): ${input.correct} of ${input.answered} correct${input.answered < input.total ? ` (${input.total} questions in the drill)` : ''}.`
  lines.push(result)

  if (f.misses.length > 0) {
    lines.push('Missed:')
    for (const m of f.misses) {
      const bits = [
        m.difficulty ? LEVEL_WORD[m.difficulty] : null,
        m.seconds != null ? `${m.seconds}s` : null,
      ].filter(Boolean)
      lines.push(
        `- Q${m.position}${bits.length ? ` (${bits.join(', ')})` : ''}${m.stem ? `: "${m.stem}"` : ''}`,
      )
    }
  }
  if (f.patterns.length > 0) {
    lines.push('What the misses have in common, from the data:')
    for (const p of f.patterns) lines.push(`- ${p}`)
  } else if (f.misses.length > 1) {
    lines.push(
      'No pattern stands out in the timing or difficulty: name the pattern from the missed questions above if one is there, and say so plainly if it isn’t.',
    )
  }

  lines.push(
    `What it establishes — say this first, plainly, in your own words: ${establishes(input, decision)}`,
  )
  const caveat = evidenceCaveat(input, decision)
  if (caveat) lines.push(`Evidence: ${caveat}`)

  if (f.canOffer) {
    lines.push(
      `Next step — decided by the app's rule, not by you: ${nextStepWhy(decision.next)} Call offer_activity with ${JSON.stringify(offerArgs(decision.next))} in this reply. Offer exactly this ONE activity — no second card, no different kind — unless the student asks for something else. It is a new activity, not a redo, so the ALREADY DONE rules don't block it. If offer_activity refuses, say so in a sentence and offer the closest thing it allows.`,
    )
  } else {
    lines.push(
      'There is no next-step card for this drill (its topic is not one offer_activity knows). Suggest the next step in words and ask what they want to do.',
    )
  }
  lines.push(
    'Keep it short: the verdict, the pattern (if any), the one next step. No score projections.',
  )
  return lines.join('\n')
}

/* ------------------------------------------------------------------ */
/*  The student's side                                                  */
/* ------------------------------------------------------------------ */

/**
 * The message "What does this mean?" sends for the student. It names the topic
 * in full: offer_activity checks its topic against the student's own words,
 * and a message that names the drill's topic lets the next step through.
 */
export function followThroughMessage(topic: string | null): string {
  const drill = topic ? `the ${topic} drill` : 'your drill'
  return `I just finished ${drill}. What does my result tell you, and what should I do next?`
}

/** The opening of a stem, for the missed-question lines. */
export function missStem(stem: string | null | undefined): string | null {
  return stemExcerpt(stem)
}
